// My Services → "Upload services (CSV)": choose a service, download its template (pre-filled with
// the vendor's current offerings of it), fill it in a spreadsheet, upload. The file is checked
// first (preview with row-numbered problems), then imported. Photos and documents are added on
// the page afterwards; the result shows what the service still needs before its contract.
import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert, Stack, Typography, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, LinearProgress, Box, Collapse, Paper, Step, Stepper, StepLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { VENDOR_SERVICE_CSV_TEMPLATE_QUERY, IMPORT_VENDOR_SERVICE_CSV_MUTATION } from '../graphql/queries';

const download = (fileName, text) => {
  // BOM so Excel opens the ₹ and Tamil text correctly
  const url = URL.createObjectURL(new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = fileName; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function VendorServiceCsvDialog({ open, onClose, vendorUserId, serviceOptions, initialCategory, onImported, onOpenOffering }) {
  const context = vendorUserId ? { headers: { 'X-Vendor-User-Id': vendorUserId } } : undefined;
  const [category, setCategory] = useState(initialCategory || '');
  const [file, setFile] = useState(null);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [getTemplate, { data: tplData, loading: tplLoading, error: tplError }] = useLazyQuery(VENDOR_SERVICE_CSV_TEMPLATE_QUERY, { fetchPolicy: 'network-only', context });
  const [run, { loading: running, error: runError, reset: resetRun }] = useMutation(IMPORT_VENDOR_SERVICE_CSV_MUTATION, { context });
  const tpl = tplData?.vendorServiceCsvTemplate;
  const label = serviceOptions.find(s => s.category === category)?.displayName || category;

  useEffect(() => { if (open) setCategory(initialCategory || ''); }, [open, initialCategory]);
  useEffect(() => { if (category) getTemplate({ variables: { category } }); }, [category, getTemplate]);
  const clearFile = () => { setFile(null); setCsv(''); setPreview(null); setResult(null); resetRun(); };
  const close = () => { clearFile(); onClose(); };

  const choose = async (f) => {
    clearFile();
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setPreview({ ok: false, errors: [{ row: '-', message: 'The file is larger than 5 MB' }], offerings: [] }); return; }
    const text = await f.text();
    setFile(f); setCsv(text);
    const { data } = await run({ variables: { category, csv: text, dryRun: true } }).catch(() => ({ data: null }));
    if (data) setPreview(data.importVendorServiceCsv);
  };
  const doImport = async () => {
    const { data } = await run({ variables: { category, csv, dryRun: false } });
    setResult(data.importVendorServiceCsv);
    onImported();
  };
  const step = result ? 2 : file ? 1 : 0;

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Upload services from a CSV file</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 2 }}>
          {['Download the template', 'Upload & check', 'Imported'].map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        {!result && (
          <>
            <TextField select fullWidth size="small" label="Service" value={category} onChange={e => { setCategory(e.target.value); clearFile(); }} sx={{ mb: 2 }}
              helperText="Each service has its own template with that service's details and package columns">
              {serviceOptions.map(s => <MenuItem key={s.category} value={s.category}>{s.displayName}{s.contracted ? '' : ' (not in a contract yet)'}</MenuItem>)}
            </TextField>

            {category && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                {tplLoading && <LinearProgress />}
                {tplError && <Alert severity="error">{tplError.message}</Alert>}
                {tpl && (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <b>1.</b> {tpl.existing ? `Download your ${tpl.existing} current ${label} offering(s) to edit` : `Download the ${label} template`}, fill it in Excel or Google Sheets
                        and save it as CSV. One row per {tpl.columns.find(c => /name \*$/.test(c.header) && !/^Offering/.test(c.header) && !/Business/.test(c.header))?.header.replace(/ name \*$/, '').toLowerCase() || 'item'} of a package; columns marked * are required.
                      </Typography>
                      <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => download(tpl.fileName, tpl.csv)}>Download template</Button>
                    </Stack>
                    <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowGuide(g => !g)}>{showGuide ? 'Hide' : 'Show'} what each column means</Button>
                    <Collapse in={showGuide}>
                      <Box sx={{ maxHeight: 260, overflow: 'auto', mt: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead><TableRow><TableCell>Column</TableCell><TableCell>What to enter</TableCell><TableCell>Example</TableCell></TableRow></TableHead>
                          <TableBody>
                            {tpl.columns.map(c => (
                              <TableRow key={c.header}>
                                <TableCell sx={{ fontWeight: c.required ? 700 : 400, whiteSpace: 'nowrap' }}>{c.header}</TableCell>
                                <TableCell><Typography variant="caption">{c.help}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{c.example}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        Rows starting with # are ignored (the guide and the examples). Repeat the offering title and the package name on each row of the same package;
                        the other offering columns only need filling once. Lists use ; between values.
                      </Typography>
                    </Collapse>
                  </>
                )}
              </Paper>
            )}

            {category && (
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography variant="body2" sx={{ flex: 1 }}><b>2.</b> Upload the filled file. It is checked first; nothing is saved until you import.</Typography>
                <input id="service-csv-file" type="file" accept=".csv,text/csv" hidden onChange={e => { choose(e.target.files?.[0]); e.target.value = ''; }} />
                <label htmlFor="service-csv-file">
                  <Button component="span" variant="contained" startIcon={<UploadFileIcon />} disabled={running}>{file ? 'Choose another file' : 'Choose CSV file'}</Button>
                </label>
              </Stack>
            )}
            {running && <LinearProgress sx={{ my: 1 }} />}
            {runError && <Alert severity="error" sx={{ my: 1 }}>{runError.message}</Alert>}

            {preview && (
              <Box mt={1}>
                {file && <Typography variant="caption" color="text.secondary">{file.name}</Typography>}
                {preview.offerings.length > 0 && (
                  <Table size="small" sx={{ my: 1 }}>
                    <TableHead><TableRow><TableCell>Offering</TableCell><TableCell>Will be</TableCell><TableCell align="center">Packages</TableCell><TableCell align="center">Items</TableCell><TableCell align="center">Photos kept</TableCell></TableRow></TableHead>
                    <TableBody>
                      {preview.offerings.map(o => (
                        <TableRow key={o.title}>
                          <TableCell>{o.title}</TableCell>
                          <TableCell><Chip size="small" color={o.ok ? (o.action === 'create' ? 'primary' : 'info') : 'error'} label={o.ok ? (o.action === 'create' ? 'Added' : 'Updated') : 'Has problems'} /></TableCell>
                          <TableCell align="center">{o.packages}</TableCell>
                          <TableCell align="center">{o.items}</TableCell>
                          <TableCell align="center">{o.keptPhotos}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {preview.errors.length > 0 ? (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight={700} mb={0.5}>Fix these in the file and upload it again:</Typography>
                    {preview.errors.map((e, i) => <Typography key={i} variant="body2">Row {e.row}{e.offering ? ` (${e.offering})` : ''}: {e.message}</Typography>)}
                  </Alert>
                ) : <Alert severity="success">The file is correct. Import it to save {preview.offerings.length} offering(s).</Alert>}
              </Box>
            )}
          </>
        )}

        {result && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {label}: {result.created} offering(s) added, {result.updated} updated.
            </Alert>
            {result.readiness && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" mb={0.5}>{result.readiness.ready ? `${label} is complete` : `Still needed for ${label} before its contract`}</Typography>
                {result.readiness.checks.map(c => (
                  <Stack key={c.key} direction="row" spacing={1} alignItems="flex-start" py={0.25}>
                    {c.done ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                    <Box>
                      <Typography variant="body2">{c.label}</Typography>
                      {!c.done && c.hint && <Typography variant="caption" color="text.secondary">{c.hint}</Typography>}
                    </Box>
                  </Stack>
                ))}
                {result.readiness.checks.some(c => !c.done && c.key === 'photos') && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>Photos are added on the page: open the offering and upload a photo for each item.</Typography>
                )}
              </Paper>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result?.readiness?.offeringId && !result.readiness.ready && (
          <Button onClick={() => { const id = result.readiness.offeringId; close(); onOpenOffering(id); }}>Add photos now</Button>
        )}
        <Button onClick={close}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && <Button variant="contained" disabled={!preview?.ok || running} onClick={doImport}>Import</Button>}
      </DialogActions>
    </Dialog>
  );
}
